import { useEffect, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile.ts";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Stack,
    Divider,
    IconButton,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tabs,
    Tab,
    Box,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    XAxis,
    YAxis,
    Legend,
} from "recharts";
import axiosClient from "../api/axiosClient";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7f50", "#0088FE", "#00C49F"];

// Types for match statistics
interface MatchResultDTO {
    id: number;
    date: string;
    opponent: string;
    result: string;
    pointsScored: number;
    pointsConceded: number;
    competitionType: string;
}

interface PlayerMatchStats {
    playerId: number;
    nickname: string;
    matchesPlayed: number;
    matchesWon: number;
    matchesLost: number;
    setsWon: number;
    setsLost: number;
    winRate: number;
}

interface MatchSummaryDTO {
    totalMatches: number;
    matchesWon: number;
    matchesLost: number;
    matchesDrawn?: number;
    totalSetsWon: number;
    totalSetsLost: number;
    totalPointsScored: number;
    totalPointsConceded: number;
    winRate: number;
    recentMatches: MatchResultDTO[];
    playerRanking: PlayerMatchStats[];
}

export function TopPlayersCard({ data }: { data: any[] }) {
    const topPlayers = data.map((p) => ({
        nickname: p.nickname,
        form: Math.round(p.form * 100),
        skill: Math.round(p.scoreSkill * 100),
    }));

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Top Players
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                        data={topPlayers}
                        margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                    >
                        <XAxis dataKey="nickname" />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                        <Tooltip
                            formatter={(value) => `${value}%`}
                            labelStyle={{ fontWeight: 600 }}
                        />
                        <Legend />
                        <Bar
                            dataKey="form"
                            fill="#4caf50"
                            barSize={35}
                            radius={[5, 5, 0, 0]}
                            name="Form"
                        />
                        <Bar
                            dataKey="skill"
                            fill="#1976d2"
                            barSize={35}
                            radius={[5, 5, 0, 0]}
                            name="Skill"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}

export default function DashboardDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState<any>(null);
    const [topPlayers, setTopPlayers] = useState<any[]>([]);
    const [formTrend, setFormTrend] = useState<any[]>([]);
    const [distribution, setDistribution] = useState<any>(null);
    const [trainingResults, setTrainingResults] = useState<any[]>([]);
    const [matchSummary, setMatchSummary] = useState<MatchSummaryDTO | null>(null);
    const [matchLoading, setMatchLoading] = useState(false);
    const isMobile = useIsMobile();
    const [tab, setTab] = useState(0);

    // Load team data (optimized - loads only when team tab is active)
    useEffect(() => {
        if (open && tab === 0) {
            setLoading(true);
            Promise.all([
                axiosClient.get("/dashboard/summary"),
                axiosClient.get("/dashboard/top-players"),
                axiosClient.get("/dashboard/form-trend"),
                axiosClient.get("/dashboard/distribution"),
                axiosClient.get("/dashboard/training-results"),
            ])
                .then(([summaryRes, top, trend, dist, results]) => {
                    setSummary(summaryRes.data);
                    setTopPlayers(top.data);
                    setFormTrend(trend.data);
                    setDistribution(dist.data);
                    setTrainingResults(results.data);
                })
                .finally(() => setLoading(false));
        }
    }, [open, tab]);

    // Load match data separately when match tab is selected
    useEffect(() => {
        if (tab === 1 && !matchSummary && !matchLoading) {
            setMatchLoading(true);
            axiosClient.get("/dashboard/match-summary")
                .then((res) => setMatchSummary(res.data))
                .catch(() => setMatchSummary(null))
                .finally(() => setMatchLoading(false));
        }
    }, [tab, matchSummary, matchLoading]);

    const renderTeamOverview = () => {
        if (loading) {
            return (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            );
        }

        return (
            <Grid container spacing={2}>
                {/* SUMMARY */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Team Summary</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Players:</b> {summary.totalPlayers}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Age:</b> {summary.avgAge.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Attack:</b> {summary.avgAttack.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Defense:</b> {summary.avgDefense.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Serve:</b> {summary.avgServe.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Wall:</b> {summary.avgWall.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Personality:</b> {summary.avgPersonality.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Setting:</b> {summary.avgSetting.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Seniority:</b> {summary.avgSeniority.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Form:</b> {summary.avgForm.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Trainings:</b> {summary.totalTrainings}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Total Sets:</b> {summary.totalSetsPlayed.toFixed(0)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Total Score:</b> {summary.totalScore.toFixed(0)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Score Diff:</b> {summary.avgScoreDiff.toFixed(1)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Max Score Diff:</b> {summary.maxScoreDiff.toFixed(0)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Min Presence:</b> {summary.minPresence.toFixed(0)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Max Presence:</b> {summary.maxPresence.toFixed(0)}</Typography></Grid>
                                <Grid size={{ xs: 3, md: 2 }}><Typography><b>Avg Presence:</b> {summary.avgPresence.toFixed(1)}</Typography></Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* FORM TREND */}
                <Grid size={{ xs: 6, sm: 6, md: 3 }} >
                    <Card className="h-full">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Form Trend</Typography>
                            <ResponsiveContainer width="100%" height={250}>
                                <LineChart data={formTrend}>
                                    <XAxis dataKey="date" />
                                    <YAxis domain={[0, 100]} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="avgForm" stroke="#8884d8" strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* TOP PLAYERS */}
                <Grid size={{ xs: 6, md: 3 }}>
                    <TopPlayersCard data={topPlayers} />
                </Grid>

                {/* DISTRIBUTION */}
                {distribution && (
                    <>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Roles Distribution</Typography>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={Object.entries(distribution.roles).map(([name, value]) => ({ name, value }))}
                                                cx="50%" cy="50%" outerRadius={80}
                                                dataKey="value" label
                                            >
                                                {Object.keys(distribution.roles).map((_, i) => (
                                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 6, md: 3 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Gender Distribution</Typography>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={Object.entries(distribution.gender).map(([name, value]) => ({ name, value }))}
                                                cx="50%" cy="50%" outerRadius={80}
                                                dataKey="value" label
                                            >
                                                {Object.keys(distribution.gender).map((_, i) => (
                                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 6, md: 3 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Age Distribution</Typography>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={Object.entries(distribution.ageGroups).map(([name, value]) => ({ name, value }))}
                                                cx="50%" cy="50%" outerRadius={80}
                                                dataKey="value" label
                                            >
                                                {Object.keys(distribution.ageGroups).map((_, i) => (
                                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}

                {/* TRAINING RESULTS */}
                <Grid size={{ xs: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Training Results</Typography>
                            <ResponsiveContainer width="100%" height={250}>
                                <LineChart data={trainingResults}>
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="won" stroke="#00C49F" name="Won Sets" />
                                    <Line type="monotone" dataKey="lost" stroke="#FF8042" name="Lost Sets" />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const renderMatchStatistics = () => {
        if (matchLoading) {
            return (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            );
        }

        if (!matchSummary) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No match data available
                </Typography>
            );
        }

        return (
            <Grid container spacing={2}>
                {/* MATCH SUMMARY CARD */}
                <Grid size={{ xs: 12 }}>
                    <Card sx={{ backgroundColor: "primary.light", color: "primary.contrastText" }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Match Summary</Typography>
                            <Divider sx={{ mb: 2, borderColor: "primary.contrastText" }} />
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6, md: 2 }}>
                                    <Typography variant="body2">Matches Played</Typography>
                                    <Typography variant="h4">{matchSummary.totalMatches}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 2 }}>
                                    <Typography variant="body2">Won</Typography>
                                    <Typography variant="h4" sx={{ color: "#10f608" }}>{matchSummary.matchesWon}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 2 }}>
                                    <Typography variant="body2">Lost</Typography>
                                    <Typography variant="h4" sx={{ color: "#f44336" }}>{matchSummary.matchesLost}</Typography>
                                </Grid>
                                {(matchSummary.matchesDrawn ?? 0) > 0 && (
                                    <Grid size={{ xs: 6, md: 2 }}>
                                        <Typography variant="body2">Drawn</Typography>
                                        <Typography variant="h4">{matchSummary.matchesDrawn}</Typography>
                                    </Grid>
                                )}
                                <Grid size={{ xs: 6, md: 2 }}>
                                    <Typography variant="body2">Win Rate</Typography>
                                    <Typography variant="h4">{matchSummary.winRate?.toFixed(1)}%</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 2 }}>
                                    <Typography variant="body2">Sets Won/Lost</Typography>
                                    <Typography variant="h4">{matchSummary.totalSetsWon} / {matchSummary.totalSetsLost}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 2 }}>
                                    <Typography variant="body2">Points +/-</Typography>
                                    <Typography variant="h4">
                                        <span style={{ color: matchSummary.totalPointsScored - matchSummary.totalPointsConceded >= 0 ? "#10f608" : "#f44336" }}>
                                            {matchSummary.totalPointsScored > 0 ? "+" : ""}{matchSummary.totalPointsScored - matchSummary.totalPointsConceded}
                                        </span>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* RECENT MATCHES */}
                {matchSummary.recentMatches && matchSummary.recentMatches.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Recent Matches</Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Opponent</TableCell>
                                                <TableCell>Result</TableCell>
                                                <TableCell>Points</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {matchSummary.recentMatches.slice(0, 5).map((match) => {
                                                const [teamWins, opponentWins] = match.result?.split("-").map(Number) || [0, 0];
                                                // pareggio possibile solo nelle amichevoli (es. 2-2)
                                                const chipColor = teamWins > opponentWins ? "success" : teamWins < opponentWins ? "error" : "default";
                                                return (
                                                    <TableRow key={match.id}>
                                                        <TableCell>{match.date}</TableCell>
                                                        <TableCell>{match.opponent}</TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label={match.result} 
                                                                color={chipColor} 
                                                                size="small" 
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {match.pointsScored}-{match.pointsConceded}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* PLAYER RANKING */}
                {matchSummary.playerRanking && matchSummary.playerRanking.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Player Rankings (Matches)</Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Player</TableCell>
                                                <TableCell align="center">Played</TableCell>
                                                <TableCell align="center">Won</TableCell>
                                                <TableCell align="center">Lost</TableCell>
                                                <TableCell align="center">Win%</TableCell>
                                                <TableCell align="center">Sets W/L</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {matchSummary.playerRanking.slice(0, 10).map((player) => (
                                                <TableRow key={player.playerId}>
                                                    <TableCell sx={{ fontWeight: "bold" }}>{player.nickname}</TableCell>
                                                    <TableCell align="center">{player.matchesPlayed}</TableCell>
                                                    <TableCell align="center" sx={{ color: "#4caf50" }}>{player.matchesWon}</TableCell>
                                                    <TableCell align="center" sx={{ color: "#f44336" }}>{player.matchesLost}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={`${player.winRate?.toFixed(0)}%`}
                                                            color={player.winRate >= 50 ? "success" : "error"}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" sx={{ color: player.setsWon > player.setsLost ? "#4caf50" : player.setsWon < player.setsLost ? "#f44336" : "inherit" }}>
                                                            {player.setsWon}/{player.setsLost}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        );
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ backgroundColor: "primary.main" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Team Dashboard</Typography>
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Tabs 
                    value={tab} 
                    onChange={(_, v) => setTab(v)} 
                    sx={{ px: 2, pt: 1, borderBottom: 1, borderColor: "divider" }}
                >
                    <Tab label="Team Overview" />
                    <Tab label="Match Statistics" />
                </Tabs>

                <Box sx={{ p: 2 }}>
                    {tab === 0 ? renderTeamOverview() : renderMatchStatistics()}
                </Box>
            </DialogContent>
        </Dialog>
    );
}
